import React, {
  useCallback,
  useEffect,
  useState,
  Suspense,
  lazy
} from 'react';
import {
  Switch,
  Route,
  useParams,
  useLocation
} from 'react-router-dom';

import { Loading } from 'wtb-components';
import {
  get,
  Logger,
  clearSession,
  getFromSession,
  setFromSession
} from 'wtb-utils';
import { getTransactionById } from 'api';
import {
  Analytics,
  DialogError,
  DialogExpired,
  Header,
  PageLayout
} from 'components';
import {
  useAppContext,
  useAutogiroContext,
  useBetalingsserviceContext,
  useBillingContext,
  useTransactionContext,
  useLocaleContext,
  useSepaContext,
  useAuthContext
} from 'context';
import {
  ACTION_STATES,
  ERROR_TYPES,
  APP_STEPS,
  TRANSACTION_STATES,
  TRANSACTION_STATUSES,
  PAYMENT_TYPES
} from 'constants.js';
import {
  Checkout,
  Finish,
  AccountAuth,
  AccountAuthRetry
} from 'pages';
import { cleanCustomerDataToFE } from 'utils';
import { useScrollRedirect } from 'hooks';
import styles from './App.module.css';

const Sign = lazy(() => import('pages/Sign'));
const PaymentMethod = lazy(() => import('pages/PaymentMethod'));
const EmailValidation = lazy(() => import('pages/EmailValidation'));
// const Finish = lazy(() => import('pages/Finish'));

const FINISH_STATES = [
  'success',
  'rejected',
  'error',
  'expired'
];

const FINISH_ROUTES = [
  '/error',
  ...FINISH_STATES.map((state) => `/:id/${state}`)
];

const App = () => {

  const { id } = useParams();
  const location = useLocation();

  const scrollableBodyRef = useScrollRedirect();

  const { setBilling } = useBillingContext();
  const autogiroContext = useAutogiroContext();
  const betalingsserviceContext = useBetalingsserviceContext();
  const sepaContext = useSepaContext();
  const {
    transaction,
    paymentPlans,
    setActions,
    setAnalyticsScript,
    setCampaign,
    setCustomer,
    setCustomerData,
    setFormRequiredFields,
    setHasSentDetails,
    setLinks,
    setMerchant,
    setOrder,
    setPayment,
    setSign,
    setPaymentPlans,
    setSelectedPaymentPlan,
    setTransaction,
    setWtbTerms
  } = useTransactionContext();
  const {
    isLoading,
    isError,
    setIsLoading,
    setAppStep,
    setAppErrorType,
    setErrorRedirectUrl,
    appErrorType
  } = useAppContext();
  const { setAuthProvider } = useAuthContext();
  const { locale, setDefaultLocale } = useLocaleContext();

  const [ initialized, setInitialized ] = useState(false);
  const [ hasFinishedInitialization, setHasFinishedInitialization ] = useState(false);
  const [ isLoadFinished, setIsLoadFinished ] = useState(false);
  const [ openExpiredDialog, setOpenExpiredDialog ] = useState(false);

  const setPaymentContextData = useCallback((context, data) => {
    if (!context) return;

    context.setAccounts?.(data.accounts ?? []);
    context.setBankInfo?.(data.bankInfo ?? {});
    context.setBanks?.(data.banks);
    context.setMainBanks?.(data.mainBanks);
    context.setIsManualProcess?.(data.isManualProcess ?? false);
  }, []);

  const initTransactionState = useCallback((data) => new Promise((resolve, reject) => {

    const _data = { ...data };

    if (_data.state === TRANSACTION_STATES.EXPIRED) {
      setAppErrorType(ERROR_TYPES.EXPIRED);
      setAppStep(APP_STEPS.ERROR);
    } else {
      setAppErrorType(null);
    }

    if (_data.actions) {
      setActions(_data.actions);
    }

    const parseBankInfo = () => {
      try {
        return JSON.parse(getFromSession('bankInfo')) || {};
        // eslint-disable-next-line no-unused-vars
      } catch (error) {
        return {};
      }
    };

    if (_data.pay?.accounts) {
      const accounts = [];

      _data.pay?.accounts.forEach((account) => {
        accounts.push(account);
      });

      [ autogiroContext, betalingsserviceContext, sepaContext ].forEach((context) => {
        context.setAccounts(accounts);
        context.setBankInfo(parseBankInfo());
      });

      delete _data.pay?.accounts;
    }

    if (_data.pay?.banks) {
      const mainBanks = [];
      const banks = [];

      _data.pay.banks.forEach((bank) => {
        banks.push(bank);

        if (bank.primary) {
          mainBanks.push(bank);
        }
      });

      const contextData = {
        accounts: _data.pay?.accounts ?? [],
        bankInfo: {
          ...(parseBankInfo()),
          ...(_data.pay?.bank ?? {})
        },
        banks: banks || [],
        mainBanks: mainBanks || [],
        isManualProcess: getFromSession('isManualAccount') === 'true'
      };

      setAuthProvider(_data.selectedPlan?.payType?.replace('_MOCK', ''));

      switch (_data.selectedPlan?.type) {
        case PAYMENT_TYPES.AUTOGIRO:
          setPaymentContextData(autogiroContext, contextData);
          break;
        case PAYMENT_TYPES.BETALINGSSERVICE:
          setPaymentContextData(betalingsserviceContext, contextData);
          break;
        case PAYMENT_TYPES.SEPA:
          setPaymentContextData(sepaContext, contextData);
          break;
        default:
          Logger.log('There is no context data for this payment:', _data.selectedPlan?.type);
          break;
      }

      delete _data.pay.banks;
    }

    let billingSet = null;
    // Session storage for billing info
    const savedBilling = JSON.parse(getFromSession('billing'));
    if (savedBilling) {
      setBilling(savedBilling);
      billingSet = savedBilling;
    } else if (_data.billing) {
      const billing = {
        ...savedBilling,
        ...cleanCustomerDataToFE({
          ..._data.billing,
          ...(_data.customer || {}),
          language: locale || _data.language
        })
      };

      setBilling(billing);
      billingSet = billing;

      delete _data.billing;
    }

    setCampaign(_data.campaign);
    delete _data.campaign;

    if (!_data.customer) {
      _data.customer = {};
    }

    _data.customer = cleanCustomerDataToFE({
      ...(_data.contact || {}),
      ...(billingSet || {}),
      ...(_data.customer || {}),
      language: _data.language
    });
    delete _data.contact;
    setCustomerData(_data.customer);
    setCustomer(_data.customer);

    // If no billing info coming but customer data is, prefill billing info with customer data
    if (!billingSet && Object.keys(_data.customer).length) {
      const _billing = { ..._data.customer };
      delete _billing.nationalId;

      setBilling(_billing);
    }

    let hasRequiredDetails = true;
    const requiredDetails = { ..._data.requiredDetails };
    if (requiredDetails?.name && (!_data.customer?.firstName || !_data.customer?.lastName)) {
      hasRequiredDetails = false;
    }
    if (requiredDetails?.nationalId && !_data.customer?.nationalId) {
      hasRequiredDetails = false;
    }

    if (requiredDetails?.email && !_data.customer?.email) {
      hasRequiredDetails = false;
    }
    if (requiredDetails?.phone && !_data.customer?.phone) {
      hasRequiredDetails = false;
    }

    if (requiredDetails?.address && (
      !_data.customer?.street
      || !_data.customer?.zipCode
      || !_data.customer?.city
      || !_data.customer?.country
    )) {
      hasRequiredDetails = false;
    }

    setHasSentDetails(hasRequiredDetails && _data.actions?.startPay === 'OPTIONAL');
    setFormRequiredFields(_data.requiredDetails || {});
    delete _data.requiredDetails;
    delete _data.customer;

    setMerchant({
      ..._data.merchant,
      logo: _data.links?.merchant?.logo,
      redirect: { ..._data.redirect }
    });
    delete _data.merchant;
    delete _data.redirect;

    setLinks(_data.links);

    setOrder(_data.order);
    delete _data.order;

    setPayment(_data.pay);
    delete _data.pay;

    if (_data.sign) setSign(_data.sign);
    delete _data.sign;

    setPaymentPlans(_data.paymentPlans);
    delete _data.paymentPlans;

    const selectedPlan = _data.selectedPlan || JSON.parse(getFromSession('paymentPlan'));
    if (selectedPlan?.payType) {
      setAuthProvider(selectedPlan.payType.replace('_MOCK', ''));
    }

    setSelectedPaymentPlan(selectedPlan);
    delete _data.selectedPlan;

    _data.id = id;
    setTransaction(_data);

    setFromSession('wtb-has-started-sign', 'false');
    autogiroContext.setIsManualProcess(getFromSession('isManualAccount') === 'true');
    sepaContext.setIsManualProcess(getFromSession('isManualAccount') === 'true');

    localStorage.setItem('checkoutId', id);

    let request;

    if (_data.links?.merchant?.terms) {
      request = get(_data.links.merchant.terms);
    } else {
      request = Promise.resolve();
    }

    request
      .then((terms) => {
        // When need to mock terms:
        // terms = terms.map((section) => ({
        //   title: {
        //     ...section.title,
        //     'sv-fi': 'test title'
        //   },
        //   content: {
        //     ...section.content,
        //     'sv-fi': 'test content'
        //   }
        // }));

        setMerchant((prev) => ({
          ...prev,
          terms
        }));

        if (_data.links?.merchant?.info) {
          return get(_data.links.merchant.info);
        } else {
          return Promise.resolve();
        }
      })
      .then((info) => {
        // When need to mock info:
        // info['sv-fi'] = 'test info';

        setMerchant((prev) => ({
          ...prev,
          info
        }));

        if (_data.links?.wtb?.terms) {
          return get(_data.links.wtb.terms);
        } else {
          return Promise.resolve();
        }
      })
      .then((terms) => {
        // When need to mock terms:
        // terms = terms.map((section) => ({
        //   title: {
        //     ...section.title,
        //     'sv-fi': 'test title'
        //   },
        //   content: {
        //     ...section.content,
        //     'sv-fi': 'test content'
        //   }
        // }));

        setWtbTerms(terms);

        // Temporarily overwrite BE as it is not working properly
        _data.links.merchant.analytics = _data.links.merchant.terms.replace('terms', 'analytics');

        if (_data.links?.merchant?.analytics) {
          // Done it like this to avoid everything getting broken by the CORS error
          // triggered by nginx when the file doesn't exist in the CDN
          return get(_data.links.merchant.analytics)
            .then((analytics) => Promise.resolve(analytics))
            .catch(() => Promise.resolve());
        } else {
          return Promise.resolve();
        }
      })
      .then((analytics) => {
        setAnalyticsScript(analytics);

        setHasFinishedInitialization(true);

        const actions = { ..._data.actions };

        const canSign = (
          actions.startSign === ACTION_STATES.OPTIONAL
          || actions.completeSign === ACTION_STATES.OPTIONAL
        ) && actions.completePay === ACTION_STATES.FORBIDDEN;

        const path = location.pathname.split('/');
        if (path[path.length - 1] !== 'print' && _data.state === TRANSACTION_STATES.FINISHED) {
          if (_data.status === TRANSACTION_STATUSES.OK) {
            setAppStep(APP_STEPS.SUCCESS);
          } else {
            setAppStep(APP_STEPS.ERROR);
          }
        } else if (_data.state === TRANSACTION_STATES.INCOMPLETE) {
          if (canSign && !location.pathname.includes(APP_STEPS.SIGN)) {
            setAppStep(APP_STEPS.SIGN);
          } else if (isError()) {
            setAppStep(APP_STEPS.ORDER_SUMMARY);
          }
        }

        delete _data.actions;
        delete _data.links;

        resolve(_data);
      })
      .catch((error) => {
        Logger.error(error);
        reject(error);
      });
  }), [
    id,
    locale,
    setActions,
    setAnalyticsScript,
    setCampaign,
    setCustomerData,
    setCustomer,
    setHasSentDetails,
    setFormRequiredFields,
    setMerchant,
    setLinks,
    setOrder,
    setPayment,
    setPaymentPlans,
    setSelectedPaymentPlan,
    setTransaction,
    setBilling,
    setWtbTerms,
    location.pathname,
    isError,
    setAppStep,
    setSign,
    setAppErrorType,
    autogiroContext,
    sepaContext,
    setPaymentContextData,
    setAuthProvider
  ]);

  useEffect(() => { setIsLoading(true); }, [ setIsLoading ]);

  // fetch interceptor setup
  useEffect(() => {
    if (!initTransactionState) return;

    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const [ url, config ] = args;
      let response;
      setErrorRedirectUrl(window.location.href);
      try {
        response = await originalFetch(url, config);

        const nonInterceptablePaths = [
          '/resume',
          '/terms',
          '/info',
          '/analytics',
          '/complete-sign',
          '/finish',
          '/validate-email'
        ];

        const isInterceptableUrl = () => nonInterceptablePaths.reduce(
          (acc, current) => acc && !url.includes(current),
          true
        );

        const json = () => response
          .clone()
          .json()
          .then(async (data) => {
          // config only comes when fetching data from BE, so we use that to not intercept images, js, css and such
          //
          // When to not intercept:
          // - The response has state and it's closed (so the response is a 200 and the checkout is open)
          // - The response doesn't have a state (so it's an error) and has a status (error code)
          //
          // When to intercept:
          // - The response has state and it's closed (so the response is a 200 and the checkout is closed)
          // - The response doesn't have a state (so it's an error) and doesn't have a status (shouldn't happen)
            const hasToIntercept = () => !!(config && isInterceptableUrl() && (
              (data.state && data.state !== TRANSACTION_STATES.INCOMPLETE) || (!data.state && !data.status)
            ));

            if (hasToIntercept()) {
              await initTransactionState(data);

              return {};
            } else {
              return data;
            }
          });

        const text = () => response
          .clone()
          .text()
          .then(async (data) => {
            let _data;
            const hasToIntercept = () => {
              if (!config || !isInterceptableUrl() || !data || data?.trim() === '') return false;

              _data = JSON.parse(data);

              return _data.state !== TRANSACTION_STATES.INCOMPLETE;
            };

            if (hasToIntercept()) {
              await initTransactionState(_data);

              return '{}';
            } else {
              return data;
            }
          });

        response.json = json;
        response.text = text;

        return response;

      // eslint-disable-next-line no-unused-vars
      } catch (error) {
        if (!window.navigator.onLine) {
          setAppErrorType(ERROR_TYPES.NO_INTERNET);
          setAppStep(APP_STEPS.ERROR);
        }
        return null;
      }
    };
  }, [ initTransactionState, setAppErrorType, setAppStep, setErrorRedirectUrl ]);

  useEffect(() => {
    if (!id) return;

    const sessionId = getFromSession('transactionId');

    if (sessionId !== id) {
      clearSession();
      setFromSession('transactionId', id);
    }
  }, [ id ]);

  useEffect(() => {
    if (initialized) return;

    setInitialized(true);
    window.sessionStorage.removeItem('manual-error');

    if (!id || id === 'error' || id === 'undefined') {
      setIsLoadFinished(true);
      setAppStep(APP_STEPS.ERROR);
    } else {
      setIsLoading(true);
      getTransactionById(id)
        .then(async (response) => {
          await initTransactionState(response);
        })
        .catch((error) => {
          Logger.error(error);
          setIsLoadFinished(true);
          setIsLoading(false);
          setTimeout(() => setAppStep(APP_STEPS.ERROR), 100);
        });
    }
  }, [
    id,
    initialized,
    setIsLoading,
    setAppStep,
    initTransactionState
  ]);

  useEffect(() => {
    if (!hasFinishedInitialization || locale) return;

    setDefaultLocale();
  }, [ locale, hasFinishedInitialization, setDefaultLocale ]);

  useEffect(() => {
    if (!locale || !hasFinishedInitialization) return;

    setIsLoadFinished(true);

    const timeout = setTimeout(() => setIsLoading(false), 500);


    return () => {
      clearTimeout(timeout);
    };
  }, [ locale, isLoading, hasFinishedInitialization, setIsLoading ]);

  useEffect(() => {
    if (transaction.selectedPlan || paymentPlans?.length !== 1) return;

    setTransaction({
      ...transaction,
      selectedPlan: paymentPlans[0].id
    });
  }, [ transaction, paymentPlans, setTransaction ]);

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <Header />

        <div id="scrollable-body" ref={scrollableBodyRef} className={styles.body}>
          <Suspense fallback={(
            <Loading
              size={80}
              thickness={3}
              backdrop
              isLoading={isLoading}
              aria-label="loading"
            />
          )}
          >
            <PageLayout showFooter={isLoadFinished && appErrorType !== ERROR_TYPES.NO_INTERNET}>
              <Switch>
                {FINISH_ROUTES.map((route) => (
                  isLoadFinished && <Route key={route} exact path={route} component={Finish} />
                ))}

                {/*
                  The repeated condition is there because if we do it only once and put the Routes inside a
                  diamond operator, the Switch won't do its job and more than one route can be rendered,
                  making the Checkout to appear right under the Print when the url is /:id/print, for example
                */}
                {isLoadFinished && <Route exact path="/:id/sign/:step?" component={Sign} />}
                {isLoadFinished && <Route exact path="/:id/payment/:paymentMethod/:step?" component={PaymentMethod} />}
                {isLoadFinished && <Route exact path="/:id/email-validation" component={EmailValidation} />}
                {isLoadFinished && <Route exact path="/:id/account-authorization" component={AccountAuth} />}
                {isLoadFinished && <Route exact path="/:id/account-authorization/retry" component={AccountAuthRetry} />}
                {isLoadFinished && <Route exact path="/:id/:step?" component={Checkout} />}
              </Switch>
            </PageLayout>
          </Suspense>
        </div>
      </div>

      <Loading
        id="page-loading"
        size={80}
        thickness={3}
        backdrop
        isLoading={isLoading}
        aria-label="loading"
      />

      <DialogError />
      <DialogExpired openDialog={openExpiredDialog} closeDialog={() => setOpenExpiredDialog(false)} />

      <Analytics />
    </div>
  );
};

export default App;
